import type { BillingPlanDto } from './billing-plan';

/** The Business tier, recommended to every free-trial account. */
export const FREE_TRIAL_RECOMMENDED_REMINDERS_LIMIT = 250;

const DAY_MS = 24 * 60 * 60 * 1000;
const PROJECTION_EPSILON = 1e-9;

export interface PlanRecommendationInput {
  plans: BillingPlanDto[];
  /** `null` for a free-trial account. */
  currentPlan: BillingPlanDto | null;
  sentCount: number;
  cycleStart: Date;
  cycleEnd: Date;
  now: Date;
}

type LimitedPlan = BillingPlanDto & { reminders_limit: number };

export function recommendPlan(
  input: PlanRecommendationInput,
): BillingPlanDto | null {
  if (!input.currentPlan) {
    return (
      input.plans.find(
        (plan) =>
          plan.reminders_limit === FREE_TRIAL_RECOMMENDED_REMINDERS_LIMIT,
      ) ?? null
    );
  }

  const currentLimit = input.currentPlan.reminders_limit;
  if (currentLimit == null || input.sentCount < currentLimit) {
    return null;
  }

  const higherPlans = listPlansAboveLimit(input.plans, currentLimit);
  if (higherPlans.length === 0) {
    return null;
  }

  const projected = projectCycleUsage(input);
  return (
    higherPlans.find((plan) => plan.reminders_limit >= projected) ??
    higherPlans[higherPlans.length - 1]
  );
}

/** Sent so far divided by days elapsed (at least 1), times the days in the cycle, rounded up. */
export function projectCycleUsage(
  input: Pick<
    PlanRecommendationInput,
    'sentCount' | 'cycleStart' | 'cycleEnd' | 'now'
  >,
): number {
  const startMs = input.cycleStart.getTime();
  const daysElapsed = Math.max(1, (input.now.getTime() - startMs) / DAY_MS);
  const cycleDays = (input.cycleEnd.getTime() - startMs) / DAY_MS;
  return Math.ceil(
    (input.sentCount / daysElapsed) * cycleDays - PROJECTION_EPSILON,
  );
}

function listPlansAboveLimit(
  plans: BillingPlanDto[],
  limit: number,
): LimitedPlan[] {
  return plans
    .filter(
      (plan): plan is LimitedPlan =>
        plan.reminders_limit != null && plan.reminders_limit > limit,
    )
    .sort((a, b) => a.reminders_limit - b.reminders_limit);
}
